import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, CircularProgress, IconButton, Menu, MenuItem } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { addToPipeline, updatePipelineItem } from 'store/actions/pipeline'
import { selectPipelineStages } from 'store/selectors/pipelineStages'
import { trackAddFunderToPipeline, trackChangeFunderPipelineStage } from 'utils/mixpanel'
import { Close, KeyboardArrowDown } from '@mui/icons-material'

const PipelineStageButton = ({ buttonClass, opportunity, funderId, loadData }) => {
  const dispatch = useDispatch()
  const { i18n } = useTranslation()
  const t = i18n.getResourceBundle(i18n.language)

  const [isProcessing, setIsProcessing] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState()

  const { results: pipelineStages } = useSelector(selectPipelineStages)

  if (!pipelineStages) return null

  const minOrder = Math.min(...pipelineStages.map(item => item.order))
  const firstPipelineStage = pipelineStages.find(item => item.order === minOrder)

  const handleOpen = ({ currentTarget }) => {
    setIsDropdownOpen(true)
    setAnchorEl(currentTarget)
  }

  const handleAddToPipeline = async () => {
    setIsProcessing(true)
    if (opportunity) {
      await dispatch(updatePipelineItem(firstPipelineStage.id, opportunity.id))
    } else {
      await dispatch(addToPipeline(funderId, firstPipelineStage.id))
    }
    await dispatch(loadData())
    setIsProcessing(false)

    await trackAddFunderToPipeline(funderId)
  }

  const handleChangePipelineStage = async stageId => {
    setIsDropdownOpen(false)
    setIsProcessing(true)
    await dispatch(updatePipelineItem(stageId, opportunity.id))
    await dispatch(loadData())
    setIsProcessing(false)

    const prevStageId =
      (opportunity.pipelineStage && opportunity.pipelineStage.id) || opportunity.pipelineStage
    await trackChangeFunderPipelineStage({ funderId, prevStageId, stageId })
  }

  if (opportunity && opportunity.hidden) {
    return (
      <div className="Material-cards-stages-wrapper">
        <Button className={`${buttonClass} add`} disabled={true} variant="text">
          {t.pipeline.hidden}
        </Button>
      </div>
    )
  }

  if (!opportunity || !opportunity.pipelineStage) {
    const buttonProps = {
      className: `${buttonClass} add`,
      onClick: isProcessing ? () => null : handleAddToPipeline
    }
    return (
      <div className="Material-cards-stages-wrapper">
        <Button {...buttonProps} variant="text">
          {t.pipeline.addToPipeline}
          {isProcessing && <CircularProgress size={20} />}
        </Button>
      </div>
    )
  }

  const stageId =
    (opportunity.pipelineStage && opportunity.pipelineStage.id) || opportunity.pipelineStage

  return (
    <div className="Material-cards-stages-wrapper">
      <Button className={buttonClass} onClick={isProcessing ? () => null : handleOpen}>
        {pipelineStages.find(item => item.id === stageId).name}
        {isProcessing ? <CircularProgress size={20} /> : <KeyboardArrowDown />}
      </Button>
      <Menu
        className="Material-cards__expanded-stages-menu-wrapper"
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        open={isDropdownOpen}
        onClose={() => setIsDropdownOpen(false)}
      >
        <IconButton onClick={() => setIsDropdownOpen(false)}>
          <Close />
        </IconButton>
        {pipelineStages
          .sort((a, b) => a.order - b.order)
          .map(stage => {
            return (
              <MenuItem
                key={stage.id}
                className="Material-cards__expanded-stages-item"
                onClick={() => handleChangePipelineStage(stage.id)}
              >
                {stage.name}
              </MenuItem>
            )
          })}
      </Menu>
    </div>
  )
}

export default PipelineStageButton
